import { Stack, Theme } from '@mui/material';
import { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { FormAutocompleteProps, FormLabel } from 'src/components';
import { SelectMenu, SelectMenuOption } from 'src/components/SelectMenu';
import { SidePanelRow } from 'src/components/SidePanelRow';
import { toSxArray } from 'src/utils/sx';

export type ProjectLostReasonFormValue = { id: string; label: string };

export const ProjectLostReasonSelectMenuField = ({
  field,
  disabled,
}: {
  field: FormAutocompleteProps;
  disabled?: boolean;
}) => {
  const reasonOptions = useMemo(
    () => (field.options ?? []) as ProjectLostReasonFormValue[],
    [field.options],
  );

  const selectMenuOptions = useMemo<SelectMenuOption[]>(
    () => reasonOptions.map((option) => ({ value: String(option.id), title: option.label })),
    [reasonOptions],
  );

  return (
    <SidePanelRow key={field.label}>
      <FormLabel label={field.label} />
      <Stack direction="row" width="100%" gap={1}>
        <Controller
          name={field.name}
          rules={{ required: field.required }}
          render={({ field: controllerField }) => {
            const value = (controllerField.value ??
              field.defaultValue ??
              null) as ProjectLostReasonFormValue | null;

            return (
              <SelectMenu
                label={field.label ?? 'Lost Reason'}
                options={selectMenuOptions}
                value={value?.id ? String(value.id) : null}
                onChange={(selectedId) => {
                  const selectedOption = reasonOptions.find(
                    (option) => String(option.id) === selectedId,
                  );

                  if (selectedOption) {
                    controllerField.onChange(selectedOption);
                  }
                }}
                disabled={Boolean(disabled || field.disabled)}
                searchPlaceholder="Search lost reasons"
                emptyText={
                  field.loading ? 'Loading lost reasons...' : 'No lost reasons found.'
                }
                variant="inline"
                plainOptionIcons
                sx={[
                  { width: '100%' },
                  ...toSxArray(field.sx),
                  (theme: Theme) => ({
                    [theme.breakpoints.down('sm')]: {
                      fontSize: theme.utils.important('1rem'),
                    },
                  }),
                ]}
              />
            );
          }}
        />
      </Stack>
    </SidePanelRow>
  );
};
